import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Trash2, Send, Upload, Download, Package, Eye, FileText, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

const STATUS_COLORS = {
  Draft: 'bg-gray-100 text-gray-700',
  Sent: 'bg-blue-100 text-blue-800',
  Invoiced: 'bg-purple-100 text-purple-800',
  Paid: 'bg-amber-100 text-amber-800',
  Received: 'bg-green-100 text-green-800',
  Closed: 'bg-emerald-100 text-emerald-900',
};
const STATUSES = ['Draft', 'Sent', 'Invoiced', 'Paid', 'Received', 'Closed'];

export default function PurchaseOrdersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [viewPo, setViewPo] = useState(null);
  const [invoiceDialog, setInvoiceDialog] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ file: null, invoice_number: '', invoice_date: '', invoice_amount: 0 });
  const [receiptDialog, setReceiptDialog] = useState(null);
  const [receiptForm, setReceiptForm] = useState({ file: null, payment_date: '', amount: 0 });
  const [receiveDialog, setReceiveDialog] = useState(null);
  const [receiveForm, setReceiveForm] = useState({ received_date: '', notes: '', add_to_stock: true });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (searchText) params.set('search', searchText);
      const res = await api.get(`/purchase-orders?${params}`);
      setRecords(res.data);
    } catch { toast.error('Failed to load POs'); }
    finally { setLoading(false); }
  }, [statusFilter, searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSend = async (po) => {
    if (!window.confirm(`Send PO ${po.po_number} to ${po.vendor_name}?`)) return;
    try { await api.post(`/purchase-orders/${po._id}/send`); toast.success('PO marked as sent'); fetchData(); } catch { toast.error('Failed'); }
  };

  const downloadPdf = async (po) => {
    try {
      const r = await api.get(`/purchase-orders/${po._id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${po.po_number.replace(/\//g, '-')}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('PDF failed'); }
  };

  const downloadFile = async (po, which, name) => {
    try {
      const r = await api.get(`/purchase-orders/${po._id}/file/${which}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleUploadInvoice = async () => {
    if (!invoiceDialog || !invoiceForm.file) { toast.error('Select a file'); return; }
    const fd = new FormData();
    fd.append('file', invoiceForm.file);
    fd.append('invoice_number', invoiceForm.invoice_number);
    fd.append('invoice_date', invoiceForm.invoice_date);
    fd.append('invoice_amount', String(invoiceForm.invoice_amount));
    try {
      await api.post(`/purchase-orders/${invoiceDialog._id}/upload-invoice`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Invoice uploaded'); setInvoiceDialog(null); setInvoiceForm({ file: null, invoice_number: '', invoice_date: '', invoice_amount: 0 }); fetchData();
    } catch { toast.error('Upload failed'); }
  };

  const handleUploadReceipt = async () => {
    if (!receiptDialog || !receiptForm.file) { toast.error('Select a file'); return; }
    const fd = new FormData();
    fd.append('file', receiptForm.file);
    fd.append('payment_date', receiptForm.payment_date);
    fd.append('amount', String(receiptForm.amount));
    try {
      await api.post(`/purchase-orders/${receiptDialog._id}/upload-receipt`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Receipt uploaded'); setReceiptDialog(null); setReceiptForm({ file: null, payment_date: '', amount: 0 }); fetchData();
    } catch { toast.error('Upload failed'); }
  };

  const handleReceive = async () => {
    if (!receiveDialog) return;
    try {
      const res = await api.post(`/purchase-orders/${receiveDialog._id}/receive`, receiveForm);
      const updates = res.data.stock_updates || [];
      toast.success(`Goods received. ${updates.length} stock items updated`);
      setReceiveDialog(null); setReceiveForm({ received_date: '', notes: '', add_to_stock: true }); fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete PO?')) return;
    try { await api.delete(`/purchase-orders/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="po-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Purchase Orders</h1>
          <p className="text-sm text-[#4F734F] mt-1">POs auto-generated from approved quotations</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SearchBar value={searchText} onChange={setSearchText} placeholder="Search vendor, PO no..." testId="po-search" className="!flex-none w-full sm:w-80" />
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44 h-11 input-field"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead><tr className="bg-[#1B2A1B] text-white">
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">PO #</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Date</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Vendor</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider text-right">Total</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Status</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Docs</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-8 text-olive-400">Loading...</td></tr> :
            records.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-olive-400">No purchase orders. Approve a quotation to generate one.</td></tr> :
            records.map(po => (
              <tr key={po._id} className="border-b border-[#E6EDE6] hover:bg-[#F8FAF7]" data-testid={`po-row-${po._id}`}>
                <td className="py-2 px-3 font-mono text-xs">{po.po_number}</td>
                <td className="py-2 px-3">{po.po_date}</td>
                <td className="py-2 px-3 font-medium">{po.vendor_name}</td>
                <td className="py-2 px-3 text-right font-mono">{(po.total || 0).toLocaleString('en-IN')}</td>
                <td className="py-2 px-3"><span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_COLORS[po.status] || 'bg-gray-100'}`}>{po.status}</span></td>
                <td className="py-2 px-3">
                  <div className="flex gap-1">
                    {po.vendor_invoice && <button onClick={() => downloadFile(po, 'invoice', po.vendor_invoice.filename)} className="p-1 hover:bg-purple-50 rounded-sm" title="Vendor Invoice"><FileText size={13} className="text-purple-600" /></button>}
                    {po.payment_receipt && <button onClick={() => downloadFile(po, 'receipt', po.payment_receipt.filename)} className="p-1 hover:bg-amber-50 rounded-sm" title="Payment Receipt"><Receipt size={13} className="text-amber-600" /></button>}
                  </div>
                </td>
                <td className="py-2 px-3">
                  <div className="flex gap-0.5 flex-wrap">
                    <button onClick={() => setViewPo(po)} className="p-1 hover:bg-olive-50 rounded-sm" title="View"><Eye size={13} className="text-olive-500" /></button>
                    <button onClick={() => downloadPdf(po)} className="p-1 hover:bg-olive-50 rounded-sm" title="Download PDF" data-testid={`pdf-po-${po._id}`}><Download size={13} className="text-[#D4AF37]" /></button>
                    {po.status === 'Draft' && <button onClick={() => handleSend(po)} className="p-1 hover:bg-blue-50 rounded-sm" title="Mark as Sent" data-testid={`send-po-${po._id}`}><Send size={13} className="text-blue-600" /></button>}
                    {['Sent', 'Invoiced'].includes(po.status) && <button onClick={() => { setInvoiceDialog(po); setInvoiceForm({ file: null, invoice_number: '', invoice_date: '', invoice_amount: po.total || 0 }); }} className="p-1 hover:bg-purple-50 rounded-sm" title="Upload Vendor Invoice" data-testid={`upload-invoice-${po._id}`}><Upload size={13} className="text-purple-600" /></button>}
                    {['Invoiced', 'Paid'].includes(po.status) && <button onClick={() => { setReceiptDialog(po); setReceiptForm({ file: null, payment_date: '', amount: po.total || 0 }); }} className="p-1 hover:bg-amber-50 rounded-sm" title="Upload Payment Receipt" data-testid={`upload-receipt-${po._id}`}><Receipt size={13} className="text-amber-600" /></button>}
                    {['Paid', 'Invoiced', 'Sent'].includes(po.status) && <button onClick={() => { setReceiveDialog(po); setReceiveForm({ received_date: new Date().toISOString().split('T')[0], notes: '', add_to_stock: true }); }} className="p-1 hover:bg-green-50 rounded-sm" title="Goods Received" data-testid={`receive-po-${po._id}`}><Package size={13} className="text-green-600" /></button>}
                    {isAdmin && <button onClick={() => handleDelete(po._id)} className="p-1 hover:bg-red-50 rounded-sm" title="Delete"><Trash2 size={13} className="text-red-400" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* View PO */}
      <Dialog open={!!viewPo} onOpenChange={(o) => !o && setViewPo(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl">{viewPo?.po_number} — {viewPo?.vendor_name}</DialogTitle></DialogHeader>
          {viewPo && (
            <div className="space-y-3 mt-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-[#4F734F] text-xs uppercase">Date:</span> {viewPo.po_date}</div>
                <div><span className="text-[#4F734F] text-xs uppercase">Status:</span> <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_COLORS[viewPo.status]}`}>{viewPo.status}</span></div>
                <div><span className="text-[#4F734F] text-xs uppercase">Contact:</span> {viewPo.vendor_contact || '-'}</div>
                <div><span className="text-[#4F734F] text-xs uppercase">Email:</span> {viewPo.vendor_email || '-'}</div>
              </div>
              <table className="w-full text-sm border border-[#E6EDE6]">
                <thead className="bg-[#F8FAF7]"><tr><th className="p-2 text-left">#</th><th className="p-2 text-left">Description</th><th className="p-2">Qty</th><th className="p-2">Unit</th><th className="p-2 text-right">Rate</th><th className="p-2 text-right">Amount</th></tr></thead>
                <tbody>
                  {(viewPo.items || []).map((it, i) => (
                    <tr key={i} className="border-t border-[#E6EDE6]">
                      <td className="p-2">{i + 1}</td><td className="p-2">{it.description}</td><td className="p-2 text-center">{it.qty}</td><td className="p-2 text-center">{it.unit}</td>
                      <td className="p-2 text-right font-mono">{(it.rate || 0).toLocaleString('en-IN')}</td>
                      <td className="p-2 text-right font-mono">{((it.qty || 0) * (it.rate || 0)).toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-right space-y-1">
                <div>Subtotal: <span className="font-mono">{(viewPo.subtotal || 0).toLocaleString('en-IN')}</span></div>
                {viewPo.tax ? <div>Tax: <span className="font-mono">{viewPo.tax.toLocaleString('en-IN')}</span></div> : null}
                <div className="font-semibold text-lg">Total: <span className="font-mono">INR {(viewPo.total || 0).toLocaleString('en-IN')}</span></div>
              </div>
              {viewPo.sent_at && <div className="text-xs text-[#4F734F]">Sent on {viewPo.sent_at.split('T')[0]} by {viewPo.sent_by}</div>}
              {viewPo.vendor_invoice && (
                <div className="border border-purple-200 bg-purple-50 rounded-sm p-2 text-xs">
                  <strong>Vendor Invoice:</strong> {viewPo.vendor_invoice.invoice_number} dated {viewPo.vendor_invoice.invoice_date} — INR {(viewPo.vendor_invoice.invoice_amount || 0).toLocaleString('en-IN')}
                </div>
              )}
              {viewPo.payment_receipt && (
                <div className="border border-amber-200 bg-amber-50 rounded-sm p-2 text-xs">
                  <strong>Payment Receipt:</strong> Paid {viewPo.payment_receipt.payment_date} — INR {(viewPo.payment_receipt.amount || 0).toLocaleString('en-IN')}
                </div>
              )}
              {viewPo.goods_received && (
                <div className="border border-green-200 bg-green-50 rounded-sm p-2 text-xs">
                  <strong>Goods Received:</strong> on {viewPo.goods_received.received_date} by {viewPo.goods_received.received_by}
                  {viewPo.goods_received.notes && <p className="mt-1">Notes: {viewPo.goods_received.notes}</p>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Invoice */}
      <Dialog open={!!invoiceDialog} onOpenChange={(o) => !o && setInvoiceDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">Upload Vendor Invoice</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <p className="text-sm text-[#4F734F]">PO: <strong>{invoiceDialog?.po_number}</strong></p>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Invoice File *</Label><Input type="file" accept="image/*,application/pdf" onChange={(e) => setInvoiceForm(p => ({ ...p, file: e.target.files?.[0] || null }))} className="input-field" data-testid="invoice-file" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Invoice Number</Label><Input value={invoiceForm.invoice_number} onChange={(e) => setInvoiceForm(p => ({ ...p, invoice_number: e.target.value }))} className="input-field" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Invoice Date</Label><Input type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm(p => ({ ...p, invoice_date: e.target.value }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Amount (INR)</Label><Input type="number" value={invoiceForm.invoice_amount} onChange={(e) => setInvoiceForm(p => ({ ...p, invoice_amount: Number(e.target.value) }))} className="input-field" /></div>
            </div>
            <div className="flex gap-2"><Button onClick={handleUploadInvoice} className="btn-primary" data-testid="confirm-upload-invoice">Upload</Button><Button variant="outline" onClick={() => setInvoiceDialog(null)}>Cancel</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Receipt */}
      <Dialog open={!!receiptDialog} onOpenChange={(o) => !o && setReceiptDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">Upload Payment Receipt</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <p className="text-sm text-[#4F734F]">PO: <strong>{receiptDialog?.po_number}</strong></p>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Receipt File *</Label><Input type="file" accept="image/*,application/pdf" onChange={(e) => setReceiptForm(p => ({ ...p, file: e.target.files?.[0] || null }))} className="input-field" data-testid="receipt-file" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Payment Date</Label><Input type="date" value={receiptForm.payment_date} onChange={(e) => setReceiptForm(p => ({ ...p, payment_date: e.target.value }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Amount (INR)</Label><Input type="number" value={receiptForm.amount} onChange={(e) => setReceiptForm(p => ({ ...p, amount: Number(e.target.value) }))} className="input-field" /></div>
            </div>
            <div className="flex gap-2"><Button onClick={handleUploadReceipt} className="btn-primary" data-testid="confirm-upload-receipt">Upload</Button><Button variant="outline" onClick={() => setReceiptDialog(null)}>Cancel</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Goods Received */}
      <Dialog open={!!receiveDialog} onOpenChange={(o) => !o && setReceiveDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">Record Goods Received</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <p className="text-sm text-[#4F734F]">PO: <strong>{receiveDialog?.po_number}</strong></p>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Received Date</Label><Input type="date" value={receiveForm.received_date} onChange={(e) => setReceiveForm(p => ({ ...p, received_date: e.target.value }))} className="input-field" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Notes</Label><Textarea value={receiveForm.notes} onChange={(e) => setReceiveForm(p => ({ ...p, notes: e.target.value }))} className="input-field" rows={3} placeholder="Condition, partial delivery info, etc." data-testid="receive-notes" /></div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={receiveForm.add_to_stock} onChange={(e) => setReceiveForm(p => ({ ...p, add_to_stock: e.target.checked }))} data-testid="add-to-stock-check" />
              <span>Auto-add line items to Stock Register</span>
            </label>
            <div className="flex gap-2"><Button onClick={handleReceive} className="btn-primary gap-2" data-testid="confirm-receive-btn"><Package size={15} /> Record Receipt</Button><Button variant="outline" onClick={() => setReceiveDialog(null)}>Cancel</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
